import { api } from '@/shared/api/client';
import { loadConfig } from '@/shared/api/config';
import { getAuth, setAuth } from '@/shared/auth/storage';
import type {
  ActivateTerminalRequest,
  CashMovementRequest,
  CashierLoginRequest,
  CashierLoginResponse,
  EBarimtResp,
  EligibleCashier,
  HoldDto,
  HoldUpsertRequest,
  PaymentTypeDto,
  PosCategoryDto,
  PosCustomerDto,
  PosCustomerSearchRequest,
  PosItemDto,
  PosItemSearchRequest,
  SalesDto,
  SalesListItem,
  SalesPostRequest,
  SalesSearchRequest,
  ShiftCloseRequest,
  ShiftDto,
  ShiftOpenRequest,
  TerminalSession,
  XReportDto,
  ZReportDto,
} from '@/shared/api/types/pos';

/**
 * POS terminal cookie-based session.
 * Cashier login → JWT in localStorage (`tsetsu.pos.cashierToken`).
 * api.client.ts-д posScope=true үед `tsetsu.pos.cashierToken`-ийг
 * Authorization-д бичнэ. POS request-д `credentials: 'include'` авна.
 */

const CASHIER_TOKEN_KEY = 'tsetsu.pos.cashierToken';
const CASHIER_NAME_KEY = 'tsetsu.pos.cashierName';
const CASHIER_ID_KEY = 'tsetsu.pos.cashierId';

export function getCashierToken(): string | null {
  return localStorage.getItem(CASHIER_TOKEN_KEY);
}

export function setCashierSession(res: CashierLoginResponse): void {
  localStorage.setItem(CASHIER_TOKEN_KEY, res.Token);
  localStorage.setItem(CASHIER_NAME_KEY, res.DisplayName);
  localStorage.setItem(CASHIER_ID_KEY, res.CashierId);
}

export function clearCashierSession(): void {
  localStorage.removeItem(CASHIER_TOKEN_KEY);
  localStorage.removeItem(CASHIER_NAME_KEY);
  localStorage.removeItem(CASHIER_ID_KEY);
}

export function getCashierDisplay(): string | null {
  return localStorage.getItem(CASHIER_NAME_KEY);
}

export function getCashierId(): string | null {
  return localStorage.getItem(CASHIER_ID_KEY);
}

/**
 * Кассирын JWT-ийн exp claim-ыг локал шалгана (signature шалгахгүй — зөвхөн
 * хугацаа). Кассирын token 12 цаг хүчинтэй тул localStorage-аас хуучин token
 * сэргээхэд server-гүйгээр хүчингүйг илрүүлж login gate-ыг шууд харуулна.
 * 60 секундын margin — сэргээснийн дараахан дуусах token-ыг мөн хүчингүйд тооцно.
 */
export function isCashierTokenValid(token: string | null): boolean {
  if (!token) return false;
  const parts = token.split('.');
  const payloadPart = parts.length === 3 ? parts[1] : undefined;
  if (!payloadPart) return false;
  try {
    const b64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64)) as { exp?: number };
    if (typeof payload.exp !== 'number') return false;
    return payload.exp * 1000 > Date.now() + 60_000;
  } catch {
    return false;
  }
}

const FINGERPRINT_KEY = 'tsetsu.pos.fingerprint';
const PRINTER_MODE_KEY = 'tsetsu.pos.printer.mode';

/**
 * POS-ийн бүх terminal/cashier-ийн local state-ыг арилгана.
 * Backend deactivate (cookie clear)-г best-effort дуудна — cookie expire
 * болсон ч client-side cleanup гүйцэтгэх ёстой.
 */
export async function resetAllPosLocalState(): Promise<void> {
  try {
    await deactivateTerminal();
  } catch {
    /* best-effort — cookie аль хэдийн алга байж болно */
  }
  clearCashierSession();
  localStorage.removeItem(FINGERPRINT_KEY);
  localStorage.removeItem(PRINTER_MODE_KEY);
}

// ── Terminal activation ──

export function activateTerminal(req: ActivateTerminalRequest): Promise<TerminalSession> {
  return api.post<TerminalSession>('/api/pos/terminal/activate', req, { posScope: true });
}

export function getTerminalSession(): Promise<TerminalSession> {
  return api.get<TerminalSession>('/api/pos/terminal/me', { posScope: true });
}

export function refreshTerminalSession(): Promise<TerminalSession> {
  return api.post<TerminalSession>('/api/pos/terminal/refresh', undefined, { posScope: true });
}

export function deactivateTerminal(): Promise<void> {
  return api.post<void>('/api/pos/terminal/deactivate', undefined, { posScope: true });
}

// ── Cashier auth ──

export function loginCashier(req: CashierLoginRequest): Promise<CashierLoginResponse> {
  return api.post<CashierLoginResponse>('/api/pos/auth/cashier', req, { posScope: true });
}

export function listEligibleCashiers(): Promise<EligibleCashier[]> {
  return api.get<EligibleCashier[]>('/api/pos/auth/cashiers', { posScope: true });
}

export function auditIdleLock(cashierId: string): Promise<void> {
  return api.post<void>(
    '/api/pos/auth/idle-lock',
    { CashierId: cashierId },
    { posScope: true },
  );
}

export function auditLogout(cashierId: string): Promise<void> {
  return api.post<void>(
    '/api/pos/auth/logout',
    { CashierId: cashierId },
    { posScope: true },
  );
}

// ── Catalog ──

export function getPaymentTypes(): Promise<PaymentTypeDto[]> {
  return api.get<PaymentTypeDto[]>('/api/pos/payment-types', {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

export function getPosCategories(): Promise<PosCategoryDto[]> {
  return api.get<PosCategoryDto[]>('/api/pos/categories', {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

export function searchPosItems(req: PosItemSearchRequest): Promise<PosItemDto[]> {
  const qs = new URLSearchParams();
  if (req.Q) qs.set('q', req.Q);
  if (req.Barcode) qs.set('barcode', req.Barcode);
  if (req.CategoryId) qs.set('categoryId', req.CategoryId);
  if (req.CustomerId) qs.set('customerId', req.CustomerId);
  if (req.Page != null) qs.set('page', String(req.Page));
  if (req.Size != null) qs.set('size', String(req.Size));
  const tail = qs.toString();
  return api.get<PosItemDto[]>(`/api/pos/items${tail ? '?' + tail : ''}`, {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

/**
 * Барааны primary зургийг blob URL болгож татна (Upload-source). `<img src>`
 * cross-origin token дамжуулдаггүй тул cashier token + terminal activation
 * header-той fetch хийгээд `URL.createObjectURL` буцаана. 404 → null (зураггүй).
 */
export async function fetchPosItemImageBlobUrl(itemId: string): Promise<string | null> {
  const cfg = await loadConfig();
  const headers = new Headers({ Accept: 'image/*' });
  const token = getCashierToken();
  if (token) headers.set('Authorization', 'Bearer ' + token);
  headers.set('X-Ts-Terminal-Activation', '1');
  const res = await fetch(cfg.apiBaseUrl + `/api/pos/items/${itemId}/image`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });
  if (!res.ok) return null;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** РД-аар байгууллагын arCustomer-ыг олох/үүсгэх (local → EBarimt fallback). */
export function lookupCompanyByRegNo(regNo: string): Promise<PosCustomerDto> {
  return api.post<PosCustomerDto>(
    '/api/pos/customers/lookup-by-reg-no',
    { RegNo: regNo },
    { posScope: true, cashierToken: getCashierToken() },
  );
}

export function searchPosCustomers(req: PosCustomerSearchRequest): Promise<PosCustomerDto[]> {
  const qs = new URLSearchParams();
  if (req.Q) qs.set('q', req.Q);
  if (req.Page != null) qs.set('page', String(req.Page));
  if (req.Size != null) qs.set('size', String(req.Size));
  const tail = qs.toString();
  return api.get<PosCustomerDto[]>(`/api/pos/customers${tail ? '?' + tail : ''}`, {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

// ── Shift ──

// Backend `/api/pos/shift/current` нь идэвхтэй ээлж байхгүй үед body=null-аар
// 200 OK буцаана. Тэр тохиолдолд null-руу хөрвүүлж шууд буцаана. Authentication
// (401) эсвэл network алдаа гарвал ApiError throw хийнэ — chissi caller (ShiftBar)
// retry хийх боломжтой ба чимээгүй "no shift" ergonomics өгөхгүй. Урьд нь
// `.catch(() => null)` нь логист бүх алдааг идэж 'Ээлж нээх' button буруу
// харагдах байсан.
export async function getCurrentShift(): Promise<ShiftDto | null> {
  const dto = (await api.get<ShiftDto | null>('/api/pos/shift/current', {
    posScope: true,
    cashierToken: getCashierToken(),
  })) as ShiftDto | null;
  return dto ?? null;
}

export function openShift(req: ShiftOpenRequest): Promise<ShiftDto> {
  return api.post<ShiftDto>('/api/pos/shift/open', req, {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

export function closeShift(req: ShiftCloseRequest): Promise<ShiftDto> {
  return api.post<ShiftDto>('/api/pos/shift/close', req, {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

export function postCashMovement(req: CashMovementRequest): Promise<void> {
  return api.post<void>('/api/pos/cash-movement', req, {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

// ── Hold (live cart + parked) ──

export function listHolds(parked?: boolean): Promise<HoldDto[]> {
  const qs = parked == null ? '' : '?parked=' + (parked ? 'true' : 'false');
  return api.get<HoldDto[]>('/api/pos/holds' + qs, {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

export function upsertHold(req: HoldUpsertRequest): Promise<HoldDto> {
  return api.put<HoldDto>(`/api/pos/holds/${req.ClientUuid}`, req, {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

export function deleteHold(clientUuid: string): Promise<void> {
  return api.del<void>(`/api/pos/holds/${clientUuid}`, {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

// ── Sales ──

export function postSale(req: SalesPostRequest): Promise<SalesDto> {
  return api.post<SalesDto>('/api/pos/sales/post', req, {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

export function getSale(id: string): Promise<SalesDto> {
  return api.get<SalesDto>(`/api/pos/sales/${id}`, {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

export function searchSales(req: SalesSearchRequest): Promise<SalesListItem[]> {
  const qs = new URLSearchParams();
  if (req.ReceiptNo) qs.set('ReceiptNo', req.ReceiptNo);
  if (req.Phone) qs.set('Phone', req.Phone);
  if (req.CustomerId) qs.set('CustomerId', req.CustomerId);
  if (req.CashierId) qs.set('CashierId', req.CashierId);
  if (req.From) qs.set('From', req.From);
  if (req.To) qs.set('To', req.To);
  if (req.Mode != null) qs.set('Mode', String(req.Mode));
  if (req.Status != null) qs.set('Status', String(req.Status));
  if (req.Page != null) qs.set('Page', String(req.Page));
  if (req.Size != null) qs.set('Size', String(req.Size));
  const tail = qs.toString();
  return api.get<SalesListItem[]>(`/api/pos/sales${tail ? '?' + tail : ''}`, {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

export function voidSale(id: string, reason?: string): Promise<SalesDto> {
  return api.post<SalesDto>(`/api/pos/sales/${id}/void`,
    { SaleId: id, Reason: reason || null },
    { posScope: true, cashierToken: getCashierToken() },
  );
}

/** E-Баримт автомат submit fail болсон үед cashier товчоор дахин оролдоно. */
export function retryEBarimt(saleId: string): Promise<EBarimtResp> {
  return api.post<EBarimtResp>(`/api/pos/sales/${saleId}/ebarimt-retry`, undefined, {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

// ── Reports ──

export function xReport(shiftId: string): Promise<XReportDto> {
  return api.get<XReportDto>(`/api/pos/reports/x?shiftId=${shiftId}`, {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

export function zReport(shiftId: string): Promise<ZReportDto> {
  return api.get<ZReportDto>(`/api/pos/reports/z?shiftId=${shiftId}`, {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

// ── Telemetry ──

export interface PosErrorPayload {
  Type?: string;
  Message: string;
  Stack?: string;
  Url?: string;
  UserAgent?: string;
  CashierId?: string;
  SessionId?: string;
}

export function logPosError(body: PosErrorPayload): Promise<void> {
  return api.post<void>('/api/pos/telemetry/error', body, {
    posScope: true,
    cashierToken: getCashierToken(),
  });
}

/** Activation token-аас machine fingerprint (browser-аас тогтвортой ID). */
export function getMachineFingerprint(): string {
  let fp = localStorage.getItem(FINGERPRINT_KEY);
  if (!fp) {
    fp =
      (crypto.randomUUID && crypto.randomUUID()) ||
      'fp-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(FINGERPRINT_KEY, fp);
  }
  // setAuth/getAuth-ийг compat-д л шалгасан — ашиглахгүй.
  void setAuth;
  void getAuth;
  return fp;
}
